import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { scrollToSection } from "../lib/scroll";

const LINKS = [
  { id: "home", label: "Home" },
  { id: "services", label: "Services" },
  { id: "packages", label: "Packages" },
  { id: "work", label: "Work" },
  { id: "process", label: "Process" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <>
      <motion.header
        data-testid="main-nav"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 2.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[min(96%,72rem)] rounded-full border transition-[background-color,border-color,box-shadow] duration-500 ${
          scrolled
            ? "bg-black/60 border-white/15 shadow-[0_8px_40px_rgba(0,0,0,0.6)] backdrop-blur-2xl"
            : "bg-white/[0.03] border-white/10 backdrop-blur-xl"
        }`}
      >
        <nav className="flex items-center justify-between px-5 sm:px-7 py-3.5">
          <button
            data-testid="nav-logo"
            onClick={() => go("home")}
            className="font-display text-sm sm:text-base font-semibold tracking-[0.3em] text-white hover:opacity-70 transition-opacity duration-300"
          >
            CLOSER.
          </button>
          <div className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="group relative text-[13px] font-medium tracking-wide text-white/60 hover:text-white transition-colors duration-300"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-white transition-[width] duration-300 group-hover:w-full" />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="nav-cta-button"
              onClick={() => go("contact")}
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2 text-[13px] font-medium text-white transition-colors duration-300 hover:bg-white hover:text-black"
            >
              Start a Project <ArrowRight size={14} />
            </button>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="lg:hidden text-white/80 p-1"
              aria-label="Menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-40 w-[min(94%,24rem)] rounded-3xl glass p-6 lg:hidden"
          >
            <div className="flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-display text-lg font-medium text-white/75 hover:text-white py-2.5 border-b border-white/5 transition-colors duration-300"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-cta-button"
                onClick={() => go("contact")}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-black"
              >
                Start a Project <ArrowRight size={15} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
